import { useCallback, useState } from 'react';
import { EditorDataV2, IEditorDataV2 } from '../types';
import { getTextInfo } from '../utils';
import { EditorTextTypeSetter } from './types';
import {
  checkIsDynamicAsset,
  checkIsSameValue,
  getDefaultTextValue,
  getDynamicPlaceholderForTextType,
} from './utils';

export type UseDynamicContentConfig = {
  isDynamicContentAvailable?: boolean;
  allowDynamicContent: boolean;
  initialEditorData: EditorDataV2;
  draftEditorData: EditorDataV2;
  onUpdateTextType: (
    newValues: Pick<IEditorDataV2, 'textType' | 'text' | 'textHtml'>,
  ) => void;
};

export type UseDynamicContentResult = {
  allowDynamicContent: boolean;
  isDynamicContentAvailable: boolean;
  isDynamicContent: boolean;
  onUpdateTextType: EditorTextTypeSetter;
  onIsDynamicContentEnabledChange: (newValue: boolean) => void;
};

/**
 * Custom hook that manages dynamic content state and behavior for text overlays.
 * Provides functionality to enable/disable dynamic content and update text types.
 *
 * @param isDynamicContentAvailable - Whether dynamic content is available.
 * @param allowDynamicContent - Whether dynamic content is allowed.
 * @param initialEditorData - The initial editor data state.
 * @param draftEditorData - The current draft editor data state.
 * @param onUpdateTextType - Callback function to update text type and content.
 */
export default function useDynamicContent({
  isDynamicContentAvailable = true,
  allowDynamicContent,
  initialEditorData,
  draftEditorData,
  onUpdateTextType,
}: UseDynamicContentConfig) {
  const [isDynamicContentEnabled, setIsDynamicContentEnabled] = useState(
    allowDynamicContent,
  );

  const { textHtml, textValue, textType } = getTextInfo(draftEditorData);
  const isDynamicContent = checkIsDynamicAsset(textHtml, textType);

  /**
   * Handles updating the text type and text content.
   * If the new text type is the same as the current one, it resets to the initial value.
   * Otherwise, it applies the dynamic text value for the selected type.
   */
  const handleUpdateTextType: EditorTextTypeSetter = useCallback(
    (newTextType): void => {
      // Return early if dynamic content is not allowed.
      if (!allowDynamicContent) {
        return;
      }

      // Check if the new text type matches the current active text type.
      const isSameValue = checkIsSameValue(
        textType,
        newTextType,
        textValue,
        isDynamicContentEnabled,
      );

      // Choose the new text value based on whether it's the same type.
      // If same, revert to initial value, otherwise apply dynamic value.
      const newTextValue = isSameValue
        ? // The initial text value from the initial editor data.
          getDefaultTextValue(initialEditorData?.get('textHtml'))
        : // The dynamic placeholder for the new text type.
          getDynamicPlaceholderForTextType(newTextType);

      // Update enabled state (disabled if reverting, enabled if applying).
      setIsDynamicContentEnabled(!isSameValue);

      // Update the text type, text, and HTML.
      onUpdateTextType({
        textType: isSameValue ? undefined : newTextType,
        text: newTextValue,
        // Replace the current text with the new text in the HTML.
        textHtml: textHtml?.replace(textValue, newTextValue),
      });
    },
    [
      allowDynamicContent,
      initialEditorData,
      isDynamicContentEnabled,
      onUpdateTextType,
      textHtml,
      textType,
      textValue,
    ],
  );

  return {
    allowDynamicContent,
    isDynamicContentEnabled,
    isDynamicContentAvailable,
    isDynamicContent,
    onUpdateTextType: handleUpdateTextType,
    onIsDynamicContentEnabledChange: setIsDynamicContentEnabled,
  };
}
